// Dashboard (legacy window/authDashboard.js), Ranking (window/ranking.js) and Flags (window/flags.js).
import { useEffect, useMemo, useState } from 'react';
import { changeCountry, logout } from '../account/player';
import { get } from '../api/client';
import { env } from '../env';
import { currentLanguage, langTag, t } from '../i18n';
import { flagImgHtml, flagSrc, sharpFlag } from '../options/assets';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { DEFAULT_FILTERS, StatsPanel, loadFilters, type Filters } from './StatsPanel';
import { Tabs, WindowFrame } from './WindowFrame';
import { withIndicator } from '../ui/busy';
import { Donut, toSlices } from '../ui/svgCharts';
import { formatWinTime } from './WinWindow';
import { DetailsPanel } from './DetailsPanel';
import FLAGS from './flags.json';

interface Panel {
  best_time?: { game_type?: string; time?: string | number };
  best_score?: { game_type?: string; score?: string | number };
  most_played?: { game_type?: string };
  most_played_chart?: Record<string, string | number> | null;
  total_wins?: number;
  avg_time?: number;
  sum_moves?: number;
}

const INFO_ROWS = ['welcome', 'created', 'last-login', 'country'] as const;
const DETAIL_ROWS = ['best-time', 'best-score', 'most-played', 'total-wins', 'avg-time', 'sum-moves'] as const;

/** One row of the Summary (owner 2026-10-31, "ładniejsze ostylowanie ... więcej informacji"): a label, a big value and a
 *  note; null when the panel has no such best (no win yet). The keys and order stay the legacy ones (data-info-type). */
function summaryRow(
  key: (typeof DETAIL_ROWS)[number],
  p: Panel,
): { label: string; value: string; note?: string | undefined } | null {
  const game = (g: string | undefined) => (g ? t(`win.${g}`) || g : '');
  const wins = p.total_wins ?? 0;
  switch (key) {
    case 'best-time':
      return p.best_time?.game_type
        ? {
            label: t('details.col.best-time'),
            value: formatWinTime(Number(p.best_time.time) || 0),
            note: game(p.best_time.game_type),
          }
        : null;
    case 'best-score':
      return p.best_score?.game_type
        ? {
            label: t('details.col.best-score'),
            value: Number(p.best_score.score).toLocaleString(),
            note: game(p.best_score.game_type),
          }
        : null;
    case 'most-played': {
      if (!p.most_played?.game_type) return null;
      const n = Number(p.most_played_chart?.[p.most_played.game_type]) || 0;
      const share = wins ? ` · ${((n / wins) * 100).toFixed(1)}%` : '';
      return {
        label: t('panel.head-most-played'),
        value: game(p.most_played.game_type),
        note: `${t('details.tile.wins')}: ${n.toLocaleString()}${share}`,
      };
    }
    case 'total-wins':
      return { label: t('details.tile.wins'), value: wins.toLocaleString() };
    case 'avg-time':
      return { label: t('details.col.avg-time'), value: formatWinTime(p.avg_time ?? 0) };
    case 'sum-moves':
      return {
        label: t('details.tile.moves'),
        value: (p.sum_moves ?? 0).toLocaleString(),
        note: wins ? t('panel.avg-moves', { n: Math.round((p.sum_moves ?? 0) / wins) }) : undefined,
      };
  }
}

/** The flag of a text with `<img src="...flags/<cc>.png" alt="">` as the SVG (options/assets.ts flagImgHtml). */
const withSharpFlag = (html: string): string =>
  html.replace(/<img src="([^"]+)" alt="">/g, (_m, src: string) => flagImgHtml(src));

/** The "most played" donut of the Panel tab: our own SVG (ui/svgCharts.tsx; owner 2026-10-31, the Google chart library
 *  is gone). Games with a win, the most played first, the eighth and further folded into "other"; the total in the
 *  hole. It fills the room its box has left (the box is the column's flex: 1 child). */
function MostPlayedChart({ chart }: { chart: Record<string, string | number> | null | undefined }) {
  const status = chart === undefined ? 'loading' : chart === null ? 'empty' : 'chart';
  if (status !== 'chart' || !chart) {
    return (
      <div id="most_played_chars" className="no_chart_data">
        {status === 'loading' ? `${t('game.loading')}...` : t('panel-no-chart-data')}
      </div>
    );
  }
  const slices = toSlices(
    Object.entries(chart).map(([k, val]) => ({ key: k, label: t(`win.${k}`) || k, value: Number(val) || 0 })),
    t('details.other'),
  );
  const total = slices.reduce((s, x) => s + x.value, 0);
  return <Donut id="most_played_chars" slices={slices} total={total} totalLabel={t('details.tile.wins')} />;
}

export function DashboardWindow({ instance }: { instance: WindowInstance }) {
  const ui = useUiState();
  const player = ui.player;
  const multiplayer = env.appTarget === 'multiplayer';
  // undefined = loading, null = the stats answer was not ok (the chart says so instead of "Loading").
  const [panel, setPanel] = useState<Panel | null | undefined>(undefined);
  const [filters, setFilters] = useState<Filters>(() => loadFilters());
  // the Details tab beside Panel (owner 2026-10-31): its data is asked for the first time it is shown
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (multiplayer) return;
    void withIndicator(() => get('stats/get.json', { target: 'panel' })).then((r) => {
      setPanel(r.result === 'ok' && r.response && typeof r.response === 'object' ? (r.response as Panel) : null);
    });
  }, [multiplayer]);
  if (!player) return null;
  const vars: Record<string, string> = {
    player_name: player.player_name,
    signup_time: player.signup_time ?? '',
    login_time: player.login_time ?? '',
    country_name: player.country_name ?? '',
    flag_src: player.flag_src,
  };
  const main = (
    <div className="dashboard_panel">
      <div className="dashboard_main">
        <div className="rows">
          <div className="row player_info">
            {INFO_ROWS.map((row) =>
              row === 'country' ? (
                <p key={row} className="info_desc" data-row-type={row}>
                  <a
                    href="#"
                    className="edit-country"
                    title={t('flags-edit-title')}
                    dangerouslySetInnerHTML={{ __html: withSharpFlag(t('auth.info-country', vars)) }}
                    onClick={(e) => {
                      e.preventDefault();
                      uiStore().openWindow('flags', { country: player.country_code });
                    }}
                  />
                </p>
              ) : (
                <p
                  key={row}
                  className={row === 'welcome' ? 'welcome_player' : 'info_desc'}
                  data-row-type={row}
                  dangerouslySetInnerHTML={{ __html: t(`auth.info-${row}`, vars) }}
                />
              ),
            )}
            {/* Small and grey, in the player box, away from Logout (owner 2026-10-25: many players are older; no
                delete by a slip). It opens a window with a tick box; the big button there is Cancel. */}
            <p className="delete_account_link">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  uiStore().openWindow('delete_account');
                }}
              >
                {t('panel.delete-link')}
              </a>
            </p>
          </div>
        </div>
        {multiplayer ? null : (
          <div className="rows">
            <div className="row">
              <p className="most_played_label">{t('panel.head-most-played')}</p>
              <MostPlayedChart chart={panel === undefined ? undefined : panel ? panel.most_played_chart : null} />
            </div>
          </div>
        )}
      </div>
      {multiplayer ? null : (
        <div className="dashboard_details">
          <div className="rows">
            <div className="row">
              <p className="medium_label">{t('panel.head-summary')}</p>
              <ul className="panel_stats">
                {DETAIL_ROWS.map((key) => {
                  if (!panel) return <li key={key} data-info-type={key} style={{ display: 'none' }} />;
                  const row = summaryRow(key, panel);
                  return row === null ? null : (
                    <li key={key} data-info-type={key}>
                      <span className="ps_label">{row.label}</span>
                      <span className="ps_value">{row.value}</span>
                      {row.note ? <span className="ps_note">{row.note}</span> : null}
                    </li>
                  );
                })}
              </ul>
              {multiplayer ? null : (
                <p className="panel_more">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActive(1);
                    }}
                  >
                    {t('panel.more-details')}
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* a real button in the window's action row (owner 2026-10-17: "większy i jak button a nie jak link");
          still the legacy <a>, the tests click `.panel_logout a` */}
      <div className="panel_logout">
        <a
          href="#"
          role="button"
          data-icon="logout"
          onClick={(e) => {
            e.preventDefault();
            uiStore().closeWindow('auth_dashboard');
            void logout();
          }}
        >
          {t('auth.logout-link')}
        </a>
      </div>
    </div>
  );
  const labels = multiplayer
    ? [t('panel.tab-panel')]
    : [t('panel.tab-panel'), t('panel.tab-details'), t('panel.tab-your-stats'), t('panel.tab-global-stats')];
  const panes = multiplayer
    ? [main]
    : [
        main,
        <DetailsPanel key="d" shown={active === 1} />,
        <StatsPanel key="p" target="personal" filters={filters} onFilters={setFilters} playerFlag={player.flag_src} />,
        <StatsPanel key="g" target="global" filters={filters} onFilters={setFilters} />,
      ];
  return (
    <WindowFrame
      name="auth_dashboard"
      header={<span dangerouslySetInnerHTML={{ __html: t('auth.info-welcome', vars) }} />}
      closing={instance.closing}
    >
      <div className="dashboard_cont">
        <DashboardTabs labels={labels} active={active} onChange={setActive}>
          {panes}
        </DashboardTabs>
      </div>
    </WindowFrame>
  );
}

/** Like Tabs, but the panes carry the legacy classes `personal` / `global` the tests read (and `details`). */
function DashboardTabs({
  labels,
  active,
  onChange,
  children,
}: {
  labels: string[];
  active: number;
  onChange: (i: number) => void;
  children: React.ReactNode[];
}) {
  const classes = ['', 'details', 'personal', 'global'];
  return (
    <>
      <div className="tab_buttons">
        {labels.map((l, i) => (
          <a
            key={i}
            href="#"
            className={i === active ? 'tab_selected' : ''}
            onClick={(e) => {
              e.preventDefault();
              onChange(i);
            }}
          >
            {l}
          </a>
        ))}
      </div>
      {children.map((c, i) => (
        <div
          key={i}
          className={`tab_contents ${classes[i] ?? ''}`.trim()}
          style={{ display: i === active ? 'block' : 'none' }}
        >
          {c}
        </div>
      ))}
    </>
  );
}

export function RankingWindow({ instance }: { instance: WindowInstance }) {
  const [filters, setFilters] = useState<Filters>(() => loadFilters());
  return (
    <WindowFrame name="ranking" header="Ranking" closing={instance.closing}>
      <div className="dashboard_cont">
        <div className="tab_contents global" style={{ display: 'block' }}>
          <StatsPanel target="global" filters={filters} onFilters={setFilters} />
        </div>
      </div>
    </WindowFrame>
  );
}

/** The stored country of a player who does not want to show one: no country has this code (server/routes/auth.js). */
const HIDDEN_COUNTRY = 'xx';

/** The country's name in the UI language (Intl.DisplayNames), else the English one of flags.json. */
function countryName(code: string, english: string): string {
  try {
    const name = new Intl.DisplayNames([langTag(currentLanguage())], { type: 'region' }).of(code.toUpperCase());
    return name && name.toUpperCase() !== code.toUpperCase() ? name : english;
  } catch {
    return english;
  }
}

/** The Flags window (owner 2026-10-31, "popraw stylowanie wyboru flag"): a list of rows with the flag and the country's
 *  name in the player's language, sorted by that name, a search box at the top; the picked country first. The entries keep
 *  the legacy markup the tests read (`a.option_item_entry.disable_bg[data-item] > img`, `title` = the English name). */
export function FlagsWindow({ instance }: { instance: WindowInstance }) {
  const current = String(instance.props.country ?? '');
  const [search, setSearch] = useState('');
  const all = useMemo(() => {
    const rows = (FLAGS as Array<[string, string]>).map(([code, name]) => ({
      code,
      name,
      local: countryName(code, name),
    }));
    const collator = new Intl.Collator(langTag(currentLanguage()));
    rows.sort((a, b) => (a.code === current ? -1 : b.code === current ? 1 : collator.compare(a.local, b.local)));
    // "Do not show my country" first (owner 2026-10-31): the code `xx` matches no country, so the grey flag with the
    // question mark shows and the address is not looked up either; the server answers it as the unknown flag "01"
    const hidden = t('flags.hidden');
    return [{ code: HIDDEN_COUNTRY, name: hidden, local: hidden }, ...rows];
  }, [current]);
  const needle = search.trim().toLocaleLowerCase();
  const rows = needle
    ? all.filter((r) => r.local.toLocaleLowerCase().includes(needle) || r.name.toLowerCase().includes(needle))
    : all;
  return (
    <WindowFrame name="flags" header={t('flags-window-header')} closing={instance.closing}>
      <div className="options_window flags_window">
        <div className="flags_search">
          <input
            type="search"
            value={search}
            placeholder={t('flags.search')}
            aria-label={t('flags.search')}
            autoComplete="off"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="tab_contents" style={{ display: 'block' }}>
          <div className="option_item radio">
            {rows.map(({ code, name, local }) => (
              <a
                key={code}
                href="#"
                className={`option_item_entry disable_bg${code === current || (code === HIDDEN_COUNTRY && current === '01') ? ' selected' : ''}`}
                data-item={code}
                title={name}
                onClick={(e) => {
                  e.preventDefault();
                  uiStore().closeWindow('flags');
                  void changeCountry(code);
                }}
              >
                <img
                  src={sharpFlag(flagSrc(code === HIDDEN_COUNTRY ? '01' : code))}
                  alt={name}
                  width={32}
                  height={24}
                  loading="lazy"
                />
                <span className="flag_name">{local}</span>
              </a>
            ))}
            {rows.length === 0 ? <p className="flags_none">{t('stats.items-not-found')}</p> : null}
          </div>
        </div>
      </div>
    </WindowFrame>
  );
}

export { DEFAULT_FILTERS, Tabs };

const escapeHtml = (s: string): string => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/**
 * A request to delete the account (owner 2026-10-25): it does not delete anything itself; it sends the request with the
 * logged-in player's name to /contact.html, where the owner reads it in /admin (the server adds "Użytkownik: <name>"
 * from the session, so no password is asked) and deletes the account by hand. Send stays greyed out until the tick
 * box is ticked; Cancel is the big blue button and has the focus.
 */
export function DeleteAccountWindow({ instance }: { instance: WindowInstance }) {
  const player = useUiState().player;
  const [sure, setSure] = useState(false);
  const [state, setState] = useState<'ask' | 'sending' | 'sent' | 'failed'>('ask');
  const close = () => uiStore().closeWindow('delete_account');
  if (!player) return null;
  const send = async () => {
    setState('sending');
    try {
      const body = new URLSearchParams({ message: `:Delete Request (Panel): ${player.player_name}`, email: '' });
      const r = await fetch(`${env.fullAddr}contact.html`, { method: 'POST', body, credentials: 'include' });
      setState(r.ok ? 'sent' : 'failed');
    } catch {
      setState('failed');
    }
  };
  const done = state === 'sent';
  return (
    <WindowFrame
      name="delete_account"
      className="lbx_confirm"
      header={t('panel.delete-header')}
      overlay
      closing={instance.closing}
    >
      <div className="tab_contents">
        <div className="rules_cont">
          {done ? (
            <p>{t('panel.delete-sent')}</p>
          ) : (
            <>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('panel.delete-text', { player_name: escapeHtml(player.player_name) }),
                }}
              />
              <label className="delete_account_sure">
                <input
                  type="checkbox"
                  checked={sure}
                  disabled={state === 'sending'}
                  onChange={(e) => setSure(e.target.checked)}
                />
                <span>{t('panel.delete-check')}</span>
              </label>
              {state === 'failed' ? <p className="delete_account_failed">{t('panel.delete-failed')}</p> : null}
            </>
          )}
        </div>
        <div className="rules_buttons">
          {done ? (
            <input key="ok" type="button" value="OK" onClick={close} />
          ) : (
            <>
              <input
                key="send"
                type="button"
                value={t('panel.delete-send')}
                disabled={!sure || state === 'sending'}
                onClick={() => void send()}
              />
              <input type="button" value={t('cancel')} autoFocus onClick={close} />
            </>
          )}
        </div>
      </div>
    </WindowFrame>
  );
}
